/**
 * LBR2-D2 careers-page — public careers API wrapper.
 *
 * Thin fetch layer over D1's UNAUTHENTICATED endpoints at `/api/v1/public/careers/*`
 * (tenant resolved server-side from hostname). Candidate identity is a SEPARATE
 * account from staff `User`; its token lives in localStorage and is sent as a
 * Bearer header on `/me` and (optionally) `/apply`.
 *
 * This module MUST NOT import the authenticated app shell / stores — the careers
 * pages are designed to lift to a standalone host (jobs.sunstellar.in) later.
 */
import type { ApplyFormInput } from './applyValidation';

const API_URL = ((import.meta as any).env?.VITE_API_URL as string | undefined) || '/api/v1';
const BASE = `${API_URL}/public/careers`;
const TOKEN_KEY = 'careers_candidate_token';
const PROFILE_KEY = 'careers_candidate';
const APPS_KEY = 'careers_applications';

export interface Opening {
  id: string;
  jobTitle: string;
  location: string;
  employmentType: string;
  minExperienceYears: string | number | null;
  maxExperienceYears: string | number | null;
  requiredSkills: string[];
  description: string | null;
  vacancies: number;
  postedDate: string | null;
  createdAt: string;
  tenantId?: string;
}

export interface Candidate {
  id: string;
  tenantId: string;
  email: string;
  fullName: string;
  phone: string | null;
}

export interface RecordedApplication {
  applicationId: string;
  jobOpeningId: string;
  jobTitle: string;
  appliedAt: string;
}

export type ApplyInput = ApplyFormInput & { jobOpeningId: string };

// ── token / profile / device-local application history ───────────────────────
export const getCandidateToken = (): string | null => {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
};
export const setCandidateToken = (t: string): void => {
  try {
    localStorage.setItem(TOKEN_KEY, t);
  } catch {
    /* ignore */
  }
};
export const clearCandidateToken = (): void => {
  try {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(PROFILE_KEY);
  } catch {
    /* ignore */
  }
};
export const getCachedCandidate = (): Candidate | null => {
  try {
    const r = localStorage.getItem(PROFILE_KEY);
    return r ? (JSON.parse(r) as Candidate) : null;
  } catch {
    return null;
  }
};
export const setCachedCandidate = (c: Candidate): void => {
  try {
    localStorage.setItem(PROFILE_KEY, JSON.stringify(c));
  } catch {
    /* ignore */
  }
};
export const getRecordedApplications = (): RecordedApplication[] => {
  try {
    const r = localStorage.getItem(APPS_KEY);
    return r ? (JSON.parse(r) as RecordedApplication[]) : [];
  } catch {
    return [];
  }
};
export const recordApplication = (a: RecordedApplication): void => {
  try {
    const all = getRecordedApplications().filter((x) => x.applicationId !== a.applicationId);
    all.unshift(a);
    localStorage.setItem(APPS_KEY, JSON.stringify(all.slice(0, 50)));
  } catch {
    /* ignore */
  }
};

// ── pure multipart builder (unit-tested) ─────────────────────────────────────
export function buildApplyFormData(input: ApplyInput, resume?: File | null): FormData {
  const fd = new FormData();
  fd.set('jobOpeningId', input.jobOpeningId);
  fd.set('applicantName', input.applicantName.trim());
  fd.set('phone', input.phone.trim());
  const optionals: (keyof ApplyFormInput)[] = [
    'email',
    'currentLocation',
    'experienceYears',
    'currentCtcLakh',
    'expectedCtcLakh',
    'noticePeriodDays',
    'notes',
  ];
  for (const k of optionals) {
    const v = (input as any)[k];
    if (v !== undefined && v !== null && String(v).trim() !== '') fd.set(k, String(v).trim());
  }
  if (resume) fd.set('resume', resume);
  return fd;
}

async function unwrap<T>(res: Response): Promise<T> {
  const json = await res.json().catch(() => ({}) as any);
  if (!res.ok || json?.success === false) {
    throw new Error(json?.error || json?.message || 'Something went wrong. Please try again.');
  }
  return json.data as T;
}

// ── openings ─────────────────────────────────────────────────────────────────
export const listOpenings = async (): Promise<Opening[]> =>
  unwrap<Opening[]>(await fetch(`${BASE}/openings`));

export const getOpening = async (id: string): Promise<Opening> =>
  unwrap<Opening>(await fetch(`${BASE}/openings/${encodeURIComponent(id)}`));

// ── candidate accounts ───────────────────────────────────────────────────────
export const registerCandidate = async (body: {
  email: string;
  password: string;
  fullName: string;
  phone?: string;
}): Promise<{ candidate: Candidate; token: string }> => {
  const data = await unwrap<{ candidate: Candidate; token: string }>(
    await fetch(`${BASE}/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    }),
  );
  setCandidateToken(data.token);
  setCachedCandidate(data.candidate);
  return data;
};

export const loginCandidate = async (body: {
  email: string;
  password: string;
}): Promise<{ candidate: Candidate; token: string }> => {
  const data = await unwrap<{ candidate: Candidate; token: string }>(
    await fetch(`${BASE}/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    }),
  );
  setCandidateToken(data.token);
  setCachedCandidate(data.candidate);
  return data;
};

export const fetchMe = async (): Promise<Candidate | null> => {
  const token = getCandidateToken();
  if (!token) return null;
  const res = await fetch(`${BASE}/me`, { headers: { Authorization: `Bearer ${token}` } });
  if (res.status === 401) {
    clearCandidateToken();
    return null;
  }
  const data = await unwrap<{ candidate: Candidate }>(res);
  setCachedCandidate(data.candidate);
  return data.candidate;
};

// ── apply (candidate token OPTIONAL) ─────────────────────────────────────────
export const applyToOpening = async (
  input: ApplyInput,
  resume?: File | null,
): Promise<{ applicationId: string; status: string }> => {
  const token = getCandidateToken();
  const res = await fetch(`${BASE}/apply`, {
    method: 'POST',
    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    body: buildApplyFormData(input, resume),
  });
  return unwrap<{ applicationId: string; status: string }>(res);
};
